<template>
  <section class="catalog-container">
    <template v-if="getAtmScreen === atmScreenEnum.CATALOG">
      <div class="atm-header">
        <atm-header />
      </div>

      <!-- Loading inicial enquanto carrega o catálogo -->
      <div
        v-if="isInitialLoading"
        class="initial-loading-container"
      >
        <div class="loading-content">
          <b-spinner
            variant="success"
            style="width: 3rem; height: 3rem"
          />
          <p class="mt-3 text-muted">
            Carregando catálogo...
          </p>
        </div>
      </div>
      <div
        v-else
        id="atm-catalog-main-container"
        ref="scrollContainer"
        class="products-container"
        @scroll="onCatalogScroll"
      >
        <atm-catalog @scrollToTop="() => onScrollToTop('atm-catalog-main-container')" />

        <div
          v-if="!isLoadedAllCatalog && !isLoadingCatalog"
          class="px-5 w-100 flex justify-content-center"
        >
          <e-button
            class="w-100"
            text="Carregar mais produtos"
            @click="() => stFetchAtmProductCatalog()"
          />
        </div>
      </div>

      <atm-cart-sidebar />
    </template>

    <template v-else-if="getAtmScreen === atmScreenEnum.PAYMENT">
      <section class="payment-container">
        <atm-payment @finish-sale="onFinishSale" />
      </section>
    </template>

    <footer
      v-if="!isInitialLoading"
      class="footer-container"
    >
      <atm-footer
        :is-busy="isFinishingSale"
        @finish-sale="onFinishSale"
      />
    </footer>

    <modal-cold-option ref="atmModalColdOption" />
  </section>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import { app, keyboardCode, loadingOverlay, saleDomain } from '@/mixins'
import { mapGetters, mapActions, mapState } from 'vuex'
import { atmScreenEnum } from '@/store/pages/pdv/atm/atm-status'
import EButton from '@/views/components/EButton.vue'
import delegablePermissions from '@/utils/delegable-permissions'
import { getInitialAtmCatalogFilters } from '@/store/pages/pdv/atm/atm-catalog'
import { trackEvent } from '@/frontend-monitoring'
import AtmFooter from './components/AtmFooter.vue'
import AtmCartSidebar from './components/AtmCartSidebar.vue'
import AtmCatalog from './components/AtmCatalog.vue'
import AtmPayment from './AtmPayment.vue'
import AtmHeader from './components/AtmHeader.vue'
import ModalColdOption from '../pay-box/components/modals/ModalColdOption.vue'

export default {
  components: {
    BSpinner,
    AtmHeader,
    AtmCatalog,
    AtmCartSidebar,
    AtmPayment,
    AtmFooter,
    EButton,
    ModalColdOption,
  },
  mixins: [app, keyboardCode, loadingOverlay, saleDomain],

  data() {
    return {
      unsubscribe: null,
      atmScreenEnum,
      isFinishingSale: false,
      isInitialLoading: true,
    }
  },

  computed: {
    ...mapState('app', ['modalAuthRef']),
    ...mapState('pages/pdv/payBox', ['coldOption']),

    ...mapGetters('pages/pdv/payBoxConfiguration', ['isKioskStore', 'currentPayboxConfiguration']),
    ...mapGetters('pages/pdv/atm/status', ['getAtmScreen']),
    ...mapGetters('pages/pdv/atm/catalog', ['isLoadedAllCatalog', 'isLoadingCatalog']),
    ...mapGetters('pages/pdv/payBox', ['isNfeRequired', 'hasColdOptionProducts']),
  },

  watch: {},

  created() {
    // Mantém o IDB session/uuid sincronizado com qualquer mutação do payBox no
    // contexto do ATM (mesmo padrão usado em PayBox.vue e AtmIdentifyCustomer.vue).
    // Sem esta subscrição, o uuid permanece null e a finalização da venda falha
    // com "The Uuid field is required".
    this.unsubscribe = this.$store.subscribe(async mutation => {
      if (mutation.type.startsWith('pages/pdv/payBox')) {
        await this.$store.dispatch('pages/pdv/payBox/updatePdvSession', {})
      }
      if (mutation.type.startsWith('pages/pdv/atm/status')) {
        await this.$store.dispatch('pages/pdv/atm/status/updateAtmStatus', {})
      }
    })
  },

  async mounted() {
    try {
      // Carrega o catálogo de produtos ao entrar na tela
      await this.stLoadAtmCatalogData()

      // Carrega a configuração de recompensas (mesmo padrão do PayBox.vue). Sem
      // isso, `reward.rewardConfiguration` fica nulo no ATM para clientes que já
      // aceitaram os termos (o RewardTermsModal — única outra fonte que carrega a
      // config — não é exibido nesse caso). O cálculo do limite de uso de ecash
      // então não encontra a configuração da tabela de preço e cai no padrão de
      // 50%, ignorando o limite específico (ex.: 30%) da tabela da venda.
      await this.stGetRewardConfiguration()

      // Carrega promoções SpendAndGet ativas da loja para o reprocessador local
      // de brindes (pay-box.reprocessSpendAndGetRewards). A regra é baseada na
      // tabela EFETIVA da venda (ver pages/pdv/effectivePriceTable). A tabela
      // do cliente sozinha não é usada — quando ela não pode reger o canal
      // (ATM com activeAtm=false, tabela inativa), a default é usada.
      const storeId = this.currentPayboxConfiguration?.store?.id
      if (storeId) {
        await this.$store.dispatch(
          'pages/pdv/effectivePriceTable/ensureDefaultPriceTable',
          { storeId }
        )
        const effective = this.$store.getters['pages/pdv/effectivePriceTable/effectivePriceTable']
        await this.$store.dispatch(
          'pages/pdv/spendAndGetPromotions/fetchActivePromotions',
          {
            storeId,
            priceTableId: effective?.id ?? null,
          }
        )
        await this.$store.dispatch('pages/pdv/payBox/reprocessSpendAndGetRewards')
      }
    } catch (error) {
      this.showError({ error })
    } finally {
      this.isInitialLoading = false
    }
  },

  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },

  methods: {
    ...mapActions('pages/pdv/atm', {
      stCleanAtmState: 'cleanState',
      stLoadAtmCatalogData: 'loadAtmCatalogData',
    }),
    ...mapActions('pages/pdv/atm/status', {
      stSetIsFinishError: 'setIsFinishError',
    }),
    ...mapActions('pages/pdv/atm/catalog', {
      stFetchAtmProductCatalog: 'fetchAtmProductCatalog',
      stSetFiltersAndSearch: 'setFiltersAndSearch',
    }),
    ...mapActions('pages/pdv/payBox', {
      stAddTempDelegation: 'AddTempDelegation',

      stFinishSale: 'finishSale',
      stValidateFinishSale: 'validateFinishSale',
    }),
    ...mapActions('reward', {
      clearRewardData: 'clearRewardData',
      stGetRewardConfiguration: 'getRewardConfiguration',
    }),

    async onFinishSale() {
      const validationResult = { title: '', message: '', isInvoiceExceededValueLimits: false }

      try {
        const finishSaleValidationResult = await this.stValidateFinishSale()
        validationResult.title = this.$t(finishSaleValidationResult.title)
        validationResult.message = this.$t(finishSaleValidationResult.message)
        validationResult.isInvoiceExceededValueLimits =
          finishSaleValidationResult.isInvoiceExceededValueLimits

        if (validationResult.message) {
          this.stSetIsFinishError(true)

          await this.showAtmAlert({
            title: validationResult.title,
            text: validationResult.message,
            icon: 'warning',
            timer: null,
          })
          return
        }

        this.isFinishingSale = true

        if (this.isNfeRequired) {
          let message = ''
          if (validationResult.isInvoiceExceededValueLimits) {
            message = 'O valor da venda ultrapassa o limite permitido para emissão do cupom fiscal.'
          } else {
            message = 'Informado na venda um cliente com CNPJ.'
          }

          await this.showAtmAlert({
            title: 'Informação',
            text: this.$t('PAY_BOX_SALE.INVOICE.ATM.CREATE_NFE_INFO', { message }),
            icon: 'info',
            timer: null,
          })

          const delegateUserData = await this.modalAuthRef.auth(
            delegablePermissions.ERP_INVOICE_GENERATE
          )
          this.stAddTempDelegation(delegateUserData)
        }

        const coldOption =
          this.coldOption ??
          (this.hasColdOptionProducts && !this.isKioskStore
            ? await this.$refs.atmModalColdOption.showModal()
            : null) ??
          this.coldOptionEnum.NATURAL

        this.showLoadingOverlay(this.$t('Finalizando compra'))
        const resultFinishSale = await this.stFinishSale(coldOption)
        await this.onCleanSale()

        await this.showAtmAlert({
          title: this.$t('Compra finalizada!'),
          text: resultFinishSale.createdSale?.rewardsAmountEarned
            ? `Você acumulou ${this.$options.filters.currency(
                resultFinishSale.createdSale.rewardsAmountEarned
              )} em ecash com esta compra!`
            : resultFinishSale.displayMessage,
          icon: 'success',
          confirmButtonText: this.$t('Ok'),
        })

        // Redireciona para a tela de identificação do cliente após finalizar a venda
        this.$router.push({ name: 'pdv-atm-identify-customer' })
      } catch (error) {
        console.error(error)
        this.stSetIsFinishError(true)

        await this.showAtmAlert({
          error,
          title: 'Não foi possível concluir a compra',
          icon: 'error',
          confirmButtonText: this.$t('Voltar'),
          timer: null,
        })

        const data = error.response?.data
        if (data?.Request && Array.isArray(data?.Request)) {
          if (data.Request.includes('SALE_ORDER.FINISH_ERROR.SALE_ALREADY_COMPLETED_OR_CANCELED')) {
            this.showLoadingOverlay(this.$t('PAY_BOX_SALE.CLOSING_ALREADY_FINISHED_OVERLAY'))
            console.log(
              'OnFinish(), Exception: SALE_ORDER.FINISH_ERROR.SALE_ALREADY_COMPLETED_OR_CANCELED \n Limpando venda atual: onCleanSale()'
            )
            await this.onCleanSale()
          }

          if (
            data.Request?.toString()?.includes(
              'PAY_BOX_SALE.DUPLICATE_UUID_WITH_DIFFERENT_CRITERIA'
            )
          ) {
            trackEvent('PayBoxSaleDuplicateUUID', {
              errorMessage: data.Request?.toString(),
            })
          }
        }
      } finally {
        this.isFinishingSale = false
        this.hideLoadingOverlay()
      }
    },

    async onCleanSale() {
      await this.stCleanAtmState()
      await this.clearRewardData()
      this.stSetFiltersAndSearch(getInitialAtmCatalogFilters())
    },

    onCatalogScroll() {
      const container = this.$refs.scrollContainer
      const heightGapToStartFetch = 100
      if (
        container.scrollHeight - container.scrollTop <=
        container.clientHeight + heightGapToStartFetch
      ) {
        this.stFetchAtmProductCatalog()
      }
    },

    onScrollToTop(elementId) {
      const rootEl = document.querySelector(`#${elementId}`)
      rootEl.scrollTo(0, 0)
    },
  },
}
</script>

<style scoped lang="scss">
.catalog-container {
  display: grid;
  height: 100vh;
  width: 100vw;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 110px 1fr 90px;

  .initial-loading-container {
    grid-column: 1 / 3;
    display: flex;
    align-items: center;
    justify-content: center;

    .loading-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }

  .atm-header {
    grid-column: 1 / 3;
    grid-row: 1 / 2;

    background-color: #222;
  }

  // catalog screen
  .products-container {
    grid-column: 1 / 3;
    grid-row: 2 / 3;

    overflow-y: auto;
  }

  // catalog screen end

  // payment screen
  .payment-container {
    grid-column: 1 / 3;
    grid-row: 1 / 3;

    overflow-y: auto;
  }

  // payment screen end

  .footer-container {
    height: 100%;
    grid-column: 1 / 3;
    grid-row: 3 / 4;
  }
}
</style>
